import { apiClient } from "@/shared/services/api";
import type { ApiResponse } from "@/features/auth/services/authApi";

export interface CropGrowthTimeline {
  id: string;
  cropType: string;
  sowingToGerminationDays: number;
  sowingToSeedlingDays: number;
  sowingToTransplantReadyDays: number;
  sowingToOvergrownDays?: number;
  totalGrowthDays: number;
}

export interface OrderItemSowingInstruction {
  id: string;
  instructionNumber: string;
  status: string;
  scheduledSowingDate: string;
  actualSowingDate: string | null;
  quantityToSow: number;
  greenhouseName: string | null;
}

export interface OrderItem {
  id: string;
  orderId: string;
  inputId?: string;
  inputName?: string;
  varietyPricingId?: string;
  isCompanySeedling: boolean;
  cropType?: string;
  cropTypeId?: string;
  seedBatchId?: string;
  seedBatchNumber?: string;
  productName: string;
  productDescription?: string;
  variety?: string;
  quantity: number;
  unit: number; // UnitOfMeasure enum
  pricingMode?: number; // 1 = PerTray, 2 = PerSeedlingOrPiece
  pricingModeName?: string;
  trayType?: number | null; // Tray catalogue identity
  trayCapacity?: number | null;
  sellablePerTray?: number | null;
  trayTypeName?: string;
  unitPrice: number;
  subTotal: number;
  discountPercentage: number;
  discountAmount: number;
  totalAmount: number;
  quantityFulfilled: number;
  quantityPending: number;
  isFullyFulfilled: boolean;
  greenhouseStockEntryId?: string;
  // Stock Advisory — snapshot captured at order placement
  fulfillmentSource: number; // 0=Unknown,1=FromStock,2=PartialStock,3=NeedsNewSowing,4=CustomerSeeds
  availableStockAtOrderTime?: number;
  stockAdvisoryNote?: string;
  hasSowingInstructions: boolean;
  sowingInstructions: OrderItemSowingInstruction[];
  notes?: string;
}

export interface Order {
  id: string;
  customerId: string;
  orderNumber: string;
  customerName: string;
  orderDate?: string;
  expectedDeliveryDate: string;
  actualDeliveryDate?: string;
  fulfilmentMethod?: "Delivery" | "CustomerCollection" | 1 | 2;
  routeStopId?: string;
  deliveryAreaId?: string | null;
  deliveryRouteId?: string;
  deliveryRouteName?: string;
  deliveryStopName?: string;
  deliveryLocation: string;
  deliveryInstructions: string;
  deliveryContactPerson: string;
  deliveryContactPhone: string;
  transportRateId?: string;
  transportDestinationName?: string;
  transportCost?: number;
  includesTrayDeposit?: boolean;
  trayDepositAmount?: number;
  trayDepositRefunded?: number;
  trayDepositRate210Hole?: number;
  trayDepositRate442Hole?: number;
  trayDepositQuantity210Hole?: number;
  trayDepositQuantity442Hole?: number;
  trayDepositRefundedQuantity210Hole?: number;
  trayDepositRefundedQuantity442Hole?: number;
  totalAmount?: number;
  paidAmount?: number;
  balanceAmount?: number;
  // Discount snapshot (frozen at order creation) — finance breakdown.
  originalAmount?: number;
  discountAmount?: number;
  discountPercentage?: number;
  /** Discount tier: "Campaign" | "Customer" | "Group" | "None". */
  discountSource?: string;
  discountReference?: string;
  discountId?: string;
  paymentStatus?: string;
  paymentDueDate?: string;
  invoiceReference?: string;
  requestedProductionStage: string;
  plannedPlantingDate: string;
  productionStageRecommendation?: string;
  specialInstructions: string;
  notes: string;
  status: string;
  cancellationReason?: string;
  cancellationDate?: string;
  isReserved?: boolean;
  reservationExpiryDate?: string;
  amendmentVersion?: number;
  hasPendingAmendment?: boolean;
  isWalkIn?: boolean;
  publishedReadyDate?: string | null;
  isReadyDatePublished?: boolean;
  readyDatePublishedAt?: string | null;
  readyDatePublishedByUserId?: string | null;
  reservationDecision?: string | null;
  reservationShortfallQuantity?: number | null;
  salesReviewedAt?: string | null;
  salesReviewedByUserId?: string | null;
  isAgronomistSubmission?: boolean;
  submittedByUserId?: string | null;
  submittedByUserName?: string | null;
  agronomistAmendmentStatus?: string;
  amendmentComment?: string | null;
  confirmedDeliveryDate?: string | null;
  confirmedPlantingDate?: string | null;
  dateConfirmedAt?: string | null;
  expoId?: string | null;
  expoName?: string | null;
  orderItems: OrderItem[];
  /**
   * Aggregate fulfillment state computed from per-line `quantityFulfilled` (#1).
   * Backend-derived; not persisted.
   */
  fulfillmentSummary?: "FullyFulfilled" | "PartiallyFulfilled" | "AwaitingFulfilment";
  createdAt: string;
  createdBy: string;
}

export interface PaymentThresholdOverride {
  id: string;
  orderId: string;
  requiredThresholdPercentage: number;
  actualPaidPercentage: number;
  requiredAmount: number;
  paidAmount: number;
  reason: string;
  status: "Pending" | "Approved" | "Rejected" | "Revoked" | "Expired" | "Superseded" | "NoLongerRequired";
  requestedAt: string;
  approvedAt?: string;
  rejectedAt?: string;
  revokedAt?: string;
  expiresAt?: string;
}

export interface PaymentThresholdOverrideQueueItem {
  id: string;
  orderId: string;
  orderNumber: string;
  customerName: string;
  requiredThresholdPercentage: number;
  actualPaidPercentage: number;
  requiredAmount: number;
  paidAmount: number;
  status: "Pending" | "Approved" | "Rejected" | "Revoked" | "Expired" | "Superseded" | "NoLongerRequired";
  reason: string;
  requestedAt: string;
  approvedAt?: string;
  rejectedAt?: string;
  revokedAt?: string;
  expiresAt?: string;
}

export interface WorkAuthorizationStatus {
  orderId: string;
  workAuthorized: boolean;
  status: string;
}

// ── Reserved Orders (Growing Manager view) ────────────────────────────────

export interface ReservationInstruction {
  sowingInstructionId: string;
  instructionNumber: string;
  status: string;
  reservedQuantity: number;
  unit: number;
  unitName: string;
  isReadyForDispatch: boolean;
  greenhouseName?: string;
}

export interface ReservedOrderItem {
  orderItemId: string;
  productName: string;
  variety?: string;
  orderedQuantity: number;
  reservedQuantity: number;
  unit: number;
  unitName: string;
  reservedInstructions: ReservationInstruction[];
}

export interface ReservedOrderSummary {
  orderId: string;
  orderNumber: string;
  customerName: string;
  orderDate: string;
  expectedDeliveryDate?: string;
  reservationExpiryDate?: string;
  plannedPlantingDate: string;
  isFullyReady: boolean;
  totalReservedQuantity: number;
  readyQuantity: number;
  notReadyQuantity: number;
  items: ReservedOrderItem[];
}

export interface CreateOrderRequest {
  specialDiscountRequest?: { kind: "Percentage" | "FixedAmount"; value: number; reason: string } | null;
  customerId: string;
  expectedDeliveryDate: string | null;
  fulfilmentMethod: "Delivery" | "CustomerCollection";
  routeStopId?: string | null;
  deliveryLocation: string;
  deliveryLatitude?: number | null;
  deliveryLongitude?: number | null;
  deliveryInstructions: string;
  deliveryContactPerson: string;
  deliveryContactPhone: string;
  requestedProductionStage?: string;
  plannedPlantingDate: string | null;
  specialInstructions: string;
  notes: string;
  transportRateId?: string | null;
  deliveryAreaId?: string | null;
  manualTransportCost?: number | null;
  includesTrayDeposit?: boolean;
  orderItems: CreateOrderItemRequest[];
}

export interface CreateOrderItemRequest {
  varietyPricingId: string;
  isCompanySeedling: boolean;
  seedBatchId?: string;
  quantity: number;
  unit: number;
  trayType?: number;
  notes?: string;
  greenhouseStockEntryId?: string | null;
}

export interface UpdateOrderRequest {
  id: string;
  expectedDeliveryDate: string;
  fulfilmentMethod?: "Delivery" | "CustomerCollection";
  routeStopId?: string | null;
  deliveryLocation: string;
  deliveryInstructions: string;
  deliveryContactPerson: string;
  deliveryContactPhone: string;
  specialInstructions: string;
  notes: string;
  transportRateId?: string | null;
  deliveryAreaId?: string | null;
  manualTransportCost?: number | null;
  /** true = add/recalculate the tray deposit, false = remove it, omit = leave unchanged. */
  includesTrayDeposit?: boolean;
}

export interface RefundTrayDepositRequest {
  trayQuantity: number;
  trayType: 210 | 442;
  refundMethod: "Wallet" | "FinancePayout";
  notes?: string;
}

export interface TrayDepositEligibility {
  approvedReturnedQuantity: number;
  refundedOrReservedQuantity: number;
  eligibleReturnedQuantity: number;
  availableQuantity210Hole: number;
  availableQuantity442Hole: number;
  rate210Hole: number;
  rate442Hole: number;
}

export interface UpdateOrderStatusRequest {
  orderId: string;
  newStatus: string;
  reason: string;
  notes: string;
}

export interface CancelOrderRequest {
  orderId: string;
  reason: string;
  notes: string;
}

export interface DispatchOrderRequest {
  orderId: string;
  dispatchDate: string;
  vehicleNumber?: string;
  driverName?: string;
  driverPhone?: string;
  deliveryNoteNumber?: string;
  notes?: string;
}

export interface ConfirmOrderWithAllocationRequest {
  decision: 1 | 3;
  reservationShortfallQuantity?: number;
  notes?: string;
}

export interface PublishReadyDateRequest {
  publishedReadyDate?: string | null;
  isPublished: boolean;
}

export interface SalesReviewStageAllocation {
  stage: string;
  availableQuantity: number;
  allocatedQuantity: number;
  stageReadyEstimate?: string | null;
}

export interface SalesReviewAllocationPreviewItem {
  orderItemId: string;
  productLabel: string;
  trayTypeLabel?: string | null;
  unitLabel?: string | null;
  requestedQuantity: number;
  allocatedQuantity: number;
  shortfallQuantity: number;
  latestReadyEstimate?: string | null;
  isLatestReadyEstimateTbd: boolean;
  stageAllocations: SalesReviewStageAllocation[];
}

export interface SalesReviewAllocationPreview {
  orderId: string;
  orderNumber: string;
  totalRequestedQuantity: number;
  totalAllocatedQuantity: number;
  totalShortfallQuantity: number;
  latestReadyEstimate?: string | null;
  isLatestReadyEstimateTbd: boolean;
  items: SalesReviewAllocationPreviewItem[];
  stageAllocations: SalesReviewStageAllocation[];
}

export interface PushBackAgronomistOrderRequest {
  comment: string;
}

// ─── Sales dispatch readiness (SPEC_SALES_DISPATCH_READINESS) ─────────────

export interface DispatchReadinessItem {
  orderItemId: string;
  productName: string;
  variety?: string | null;
  trayTypeLabel?: string | null;
  unitLabel?: string | null;
  orderedQuantity: number;
  quantityProductionReady: number;
  quantityApprovedForDispatch: number;
  quantityFulfilled: number;
  currentAvailableStock: number;
  readyToRelease: number;
  readinessStatus: "Ready" | "PartiallyReady" | "NotReady";
  availableToApprove: number;
}

export interface DispatchReadinessOrder {
  orderId: string;
  orderNumber: string;
  customerName: string;
  orderDate?: string | null;
  status: string;
  fulfilmentMethod?: "Delivery" | "CustomerCollection" | 1 | 2 | null;
  deliveryLocation?: string | null;
  itemsReadyCount: number;
  totalReadyQuantity: number;
  stockUpdatedAt: string;
  items: DispatchReadinessItem[];
}

export interface DispatchReadinessPaged {
  items: DispatchReadinessOrder[];
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
}

export interface ApproveDispatchRequest {
  method: 1 | 2;
  items: { orderItemId: string; quantity: number }[];
  orders: { orderId: string; deliveryLocation: string }[];
}

export interface ApproveDispatchResultItem {
  orderItemId: string;
  orderId: string;
  quantityProductionReady: number;
  quantityApprovedForDispatch: number;
  orderPromotedToReadyForDispatch: boolean;
}


export interface ForecastRequest {
  id: string;
  submittedByUserId: string;
  submittedByUserName: string;
  cropTypeId: string;
  cropTypeName: string;
  cropVarietyId?: string | null;
  cropVarietyName?: string | null;
  freeTextVariety?: string | null;
  details: string;
  locationName: string;
  latitude: number;
  longitude: number;
  status: string;
  acknowledgedByUserId?: string | null;
  acknowledgedAt?: string | null;
  createdAt: string;
}

export interface ForecastRequestFilters {
  agronomistUserId?: string;
  cropTypeId?: string;
  status?: string;
  fromDate?: string;
  toDate?: string;
  q?: string;
}

export interface CalculateProductionStageRequest {
  cropTypeId?: string;
  cropVarietyId?: string;
  plannedPlantingDate: string;
}

export interface ProductionStageRecommendation {
  recommendedStage: string;
  recommendedDeliveryDate: string;
  suggestedSowDate: string;
  reasoning: string;
  plantAgeAtDeliveryDays: number;
  daysRemainingForCustomer: number;
  daysUntilPlanting: number;
  acclimationDays: number;
  totalGrowthDays: number;
  alternativeStages: Array<{
    stage: string;
    stageReadyDate: string;
    daysFromSowing: number;
    suggestedSowDate: string;
    isAchievableByPlantingDate: boolean;
    description: string;
  }>;
}

export interface CreateOrderAmendmentLineChangeRequest {
  orderItemId?: string;
  action?: "Add" | "Reduce" | "Remove" | "IncreaseExistingLine" | "ReduceExistingLine" | "RemoveExistingLine" | "AddNewLine";
  varietyPricingId?: string;
  isCompanySeedling?: boolean;
  seedBatchId?: string;
  newQuantity?: number;
  quantity?: number;
  remove?: boolean;
  notes?: string;
}

export interface CreateOrderAmendmentRequest {
  expectedOrderAmendmentVersion?: number;
  reason: string;
  notes?: string;
  overpaymentDisposition?: "CustomerCreditBalance" | "RefundRequested";
  keepManualTransportCost?: boolean;
  manualTransportCost?: number;
  transportMode?: "Keep" | "Pickup" | "TransportRate" | "Manual";
  transportRateId?: string | null;
  /** Toggle the refundable tray deposit; omit to leave it unchanged. */
  includesTrayDeposit?: boolean;
  expectedDeliveryDate?: string | null;
  deliveryLocation?: string | null;
  deliveryContactPerson?: string | null;
  deliveryContactPhone?: string | null;
  lineChanges: CreateOrderAmendmentLineChangeRequest[];
  lines?: CreateOrderAmendmentLineChangeRequest[];
}

export type PreviewOrderAmendmentRequest = CreateOrderAmendmentRequest;

export interface OrderAmendmentPreviewLine {
  orderItemId?: string;
  varietyPricingId?: string;
  productName: string;
  variety?: string;
  operation: string;
  originalQuantity: number;
  proposedQuantity: number;
  quantityDifference: number;
  originalLineTotal: number;
  proposedLineTotal: number;
  notes?: string;
}

export interface OrderAmendmentPreview {
  outcome: "AmendmentAllowed" | "RequiresCancellation" | string;
  requiresCancellation: boolean;
  message?: string;
  suggestedCancellationReason?: string;
  expectedOrderAmendmentVersion: number;
  remainingActiveItemCount: number;
  originalGoodsTotal: number;
  proposedGoodsTotal: number;
  oldTransportCost: number;
  proposedTransportCost: number;
  proposedTransportRateId?: string | null;
  oldTrayDepositAmount?: number;
  proposedTrayDepositAmount?: number;
  oldExpectedDeliveryDate?: string | null;
  proposedExpectedDeliveryDate?: string | null;
  oldDeliveryLocation?: string | null;
  proposedDeliveryLocation?: string | null;
  oldDeliveryContactPerson?: string | null;
  proposedDeliveryContactPerson?: string | null;
  oldDeliveryContactPhone?: string | null;
  proposedDeliveryContactPhone?: string | null;
  oldOrderTotal: number;
  proposedOrderTotal: number;
  totalDifference: number;
  amountPaid: number;
  approvedPayments: number;
  pendingOrUnapprovedPayments: number;
  amountAlreadyAllocated: number;
  maximumAmendedOrderValue: number | null;
  canReduceToAmountPaid: boolean;
  paymentLimitApplies: boolean;
  paymentRuleMessage?: string | null;
  currentOutstandingBalance: number;
  resultingBalanceAfterAmendment: number;
  customerCreditAmount: number;
  financeReviewRequired: boolean;
  additionalAmountDue: number;
  overpayment: number;
  lines: OrderAmendmentPreviewLine[];
  validationErrors: string[];
  operationalWarnings: string[];
}

export interface SubmitOrderAmendmentRequest {
  notes?: string;
}

export interface ApproveOrderAmendmentRequest {
  approvalNotes?: string;
}

export interface RejectOrderAmendmentRequest {
  rejectionReason: string;
}

export interface ApplyOrderAmendmentRequest {
  notes?: string;
  /** Legacy amendments only; new amendments persist transport at creation. */
  keepManualTransportCost?: boolean;
  manualTransportCost?: number;
}

export interface OrderAmendmentLine {
  id: string;
  orderItemId?: string;
  action: string;
  oldQuantity?: number;
  newQuantity?: number;
  oldUnitPrice?: number;
  newUnitPrice?: number;
  oldLineTotal?: number;
  newLineTotal?: number;
  notes?: string;
}

export interface OrderAmendment {
  id: string;
  orderId: string;
  amendmentNumber: string;
  status: string;
  reason: string;
  notes?: string;
  overpaymentDisposition?: string;
  oldOrderTotal: number;
  newOrderTotal: number;
  deltaAmount: number;
  transportRateId?: string;
  oldTransportCost?: number;
  newTransportCost?: number;
  manualTransportConfirmed: boolean;
  newFulfilmentMethod?: "CustomerCollection" | "Delivery" | null;
  oldExpectedDeliveryDate?: string;
  newExpectedDeliveryDate?: string;
  oldDeliveryLocation?: string;
  newDeliveryLocation?: string;
  oldDeliveryContactPerson?: string;
  newDeliveryContactPerson?: string;
  oldDeliveryContactPhone?: string;
  newDeliveryContactPhone?: string;
  requestedByUserId?: string;
  requestedAt: string;
  approvedByUserId?: string;
  approvedAt?: string;
  appliedByUserId?: string;
  appliedAt?: string;
  lines: OrderAmendmentLine[];
}

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
}

export interface OrderAmendmentQueueItem {
  id: string;
  orderId: string;
  orderNumber: string;
  customerName: string;
  orderStatus: string;
  amendmentNumber: string;
  status: string;
  reason: string;
  notes?: string;
  deltaAmount: number;
  oldTransportCost?: number;
  newTransportCost?: number;
  newFulfilmentMethod?: "CustomerCollection" | "Delivery" | null;
  requestedAt: string;
}

export interface OrderAmendmentHistoryItem {
  id: string;
  orderId: string;
  orderNumber: string;
  customerName: string;
  amendmentNumber: string;
  status: string;
  oldOrderTotal: number;
  newOrderTotal: number;
  deltaAmount: number;
  requestedAt: string;
  approvedAt?: string;
  appliedAt?: string;
  overpaymentDisposition?: string;
  financialAction?: string;
  financialActionStatus?: string;
  financialActionQueue?: string;
  financialReference?: string;
}

export interface PagedOrders {
  items: Order[];
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
}

export interface SalesReviewQueueItem {
  id: string;
  orderNumber: string;
  customerName: string;
  totalAmount: number;
  paidAmount: number;
  balanceAmount: number;
  status: string;
  expectedDeliveryDate?: string | null;
  confirmedDeliveryDate?: string | null;
  confirmedPlantingDate?: string | null;
  publishedReadyDate?: string | null;
  isReadyDatePublished: boolean;
  reservationDecision?: string | null;
  createdAt: string;
  isAgronomistSubmission?: boolean;
  submittedByUserName?: string | null;
  agronomistAmendmentStatus?: string;
  orderItems: Array<{
    productName: string;
    variety?: string | null;
    quantity: number;
    trayTypeName?: string | null;
  }>;
}

export interface SalesReviewQueuePage {
  items: SalesReviewQueueItem[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export const ordersApi = {
  getAllOrders: async (): Promise<Order[]> => {
    const { data } = await apiClient.get<ApiResponse<Order[]>>("/Orders");
    return data.data;
  },

  getPagedOrders: async (params: {
    search?: string;
    pageNumber: number;
    pageSize: number;
  }): Promise<PagedOrders> => {
    const searchParams = new URLSearchParams();
    if (params.search) searchParams.set("search", params.search);
    searchParams.set("pageNumber", String(params.pageNumber));
    searchParams.set("pageSize", String(params.pageSize));
    searchParams.set("excludeUnresolvedSalesReview", "true");
    const { data } = await apiClient.get<ApiResponse<PagedOrders>>(
      `/Orders?${searchParams.toString()}`
    );
    return data.data;
  },

  getOrderById: async (id: string): Promise<Order> => {
    const { data } = await apiClient.get<ApiResponse<Order>>(`/Orders/${id}`);
    return data.data;
  },

  getSalesReviewQueue: async (params: {
    search?: string;
    pageNumber: number;
    pageSize: number;
  }): Promise<SalesReviewQueuePage> => {
    const searchParams = new URLSearchParams({
      unresolvedOnly: "true",
      pageNumber: String(params.pageNumber),
      pageSize: String(params.pageSize),
    });
    if (params.search) searchParams.set("search", params.search);

    const { data } = await apiClient.get<ApiResponse<SalesReviewQueuePage>>(
      `/orders/sales-review/queue?${searchParams.toString()}`
    );
    return data.data;
  },

  createOrder: async (payload: CreateOrderRequest): Promise<Order> => {
    const { data } = await apiClient.post<ApiResponse<Order>>(
      "/Orders",
      payload
    );
    return data.data;
  },

  updateOrder: async (payload: UpdateOrderRequest): Promise<Order> => {
    const { data } = await apiClient.put<ApiResponse<Order>>(
      `/Orders/${payload.id}`,
      payload
    );
    return data.data;
  },

  updateOrderStatus: async (
    payload: UpdateOrderStatusRequest
  ): Promise<Order> => {
    const { data } = await apiClient.put<ApiResponse<Order>>(
      `/Orders/${payload.orderId}/status`,
      payload
    );
    return data.data;
  },

  cancelOrder: async (payload: CancelOrderRequest): Promise<Order> => {
    const { data } = await apiClient.post<ApiResponse<Order>>(
      `/Orders/${payload.orderId}/cancel`,
      payload
    );
    return data.data;
  },

  dispatchOrder: async (payload: DispatchOrderRequest): Promise<Order> => {
    const { data } = await apiClient.post<ApiResponse<Order>>(
      `/Orders/${payload.orderId}/dispatch`,
      payload
    );
    return data.data;
  },

  getOrdersByCustomerId: async (customerId: string): Promise<Order[]> => {
    const { data } = await apiClient.get<ApiResponse<Order[]>>(
      `/Orders/customer/${customerId}`
    );
    return data.data;
  },

  getPagedOrdersByCustomerId: async (params: {
    customerId: string;
    pageNumber: number;
    pageSize: number;
  }): Promise<PagedOrders> => {
    const searchParams = new URLSearchParams();
    searchParams.set("pageNumber", String(params.pageNumber));
    searchParams.set("pageSize", String(params.pageSize));
    const { data } = await apiClient.get<ApiResponse<PagedOrders>>(
      `/Orders/customer/${params.customerId}?${searchParams.toString()}`
    );
    return data.data;
  },

  getCropGrowthTimelines: async (): Promise<CropGrowthTimeline[]> => {
    const { data } = await apiClient.get<ApiResponse<CropGrowthTimeline[]>>(
      "/Orders/crop-growth-timelines"
    );
    return data.data;
  },

  calculateProductionStage: async (
    payload: CalculateProductionStageRequest
  ): Promise<ProductionStageRecommendation> => {
    const { data } = await apiClient.post<ApiResponse<ProductionStageRecommendation>>(
      "/Orders/calculate-production-stage",
      payload
    );
    return data.data;
  },

  getOrdersReadyForSowing: async (): Promise<Order[]> => {
    const { data } = await apiClient.get<ApiResponse<Order[]>>(
      "/Orders/ready-for-sowing"
    );
    return data.data;
  },

  getPagedOrdersReadyForSowing: async (params: {
    search?: string;
    pageNumber: number;
    pageSize: number;
  }): Promise<PagedOrders> => {
    const searchParams = new URLSearchParams();
    if (params.search) searchParams.set("search", params.search);
    searchParams.set("pageNumber", String(params.pageNumber));
    searchParams.set("pageSize", String(params.pageSize));
    const { data } = await apiClient.get<ApiResponse<PagedOrders>>(
      `/Orders/ready-for-sowing?${searchParams.toString()}`
    );
    return data.data;
  },

  getPagedDispatchableOrders: async (params: {
    search?: string;
    pageNumber: number;
    pageSize: number;
  }): Promise<PagedOrders> => {
    const searchParams = new URLSearchParams();
    if (params.search) searchParams.set("search", params.search);
    searchParams.set("pageNumber", String(params.pageNumber));
    searchParams.set("pageSize", String(params.pageSize));
    const { data } = await apiClient.get<ApiResponse<PagedOrders>>(
      `/Orders/dispatchable?${searchParams.toString()}`
    );
    return data.data;
  },

  getOrdersWithActiveReservations: async (): Promise<ReservedOrderSummary[]> => {
    const { data } = await apiClient.get<ApiResponse<ReservedOrderSummary[]>>(
      "/Orders/reserved"
    );
    return data.data;
  },

  getOrdersByStatus: async (status: number): Promise<Order[]> => {
    const { data } = await apiClient.get<ApiResponse<Order[]>>(
      `/Orders/status/${status}`
    );
    return data.data;
  },

  getOrdersByDateRange: async (startDate: string, endDate: string): Promise<Order[]> => {
    const params = new URLSearchParams();
    params.set("startDate", startDate);
    params.set("endDate", endDate);
    params.set("excludeUnresolvedSalesReview", "true");
    const { data } = await apiClient.get<ApiResponse<Order[]>>(
      `/Orders/by-date-range?${params.toString()}`
    );
    return data.data;
  },

  requestPaymentThresholdOverride: async (
    orderId: string,
    reason: string
  ): Promise<PaymentThresholdOverride> => {
    const { data } = await apiClient.post<ApiResponse<PaymentThresholdOverride>>(
      `/Orders/${orderId}/payment-threshold-overrides`,
      { reason, expiresAt: null }
    );
    return data.data;
  },

  getPaymentThresholdOverrides: async (orderId: string): Promise<PaymentThresholdOverride[]> => {
    const { data } = await apiClient.get<ApiResponse<PaymentThresholdOverride[]>>(
      `/Orders/${orderId}/payment-threshold-overrides`
    );
    return data.data;
  },

  decidePaymentThresholdOverride: async (id: string, decision: "approve" | "reject", notes?: string): Promise<PaymentThresholdOverride> => {
    const { data } = await apiClient.post<ApiResponse<PaymentThresholdOverride>>(
      `/Orders/payment-threshold-overrides/${id}/${decision}`,
      { notes: notes ?? null }
    );
    return data.data;
  },

  revokePaymentThresholdOverride: async (id: string, reason: string): Promise<PaymentThresholdOverride> => {
    const { data } = await apiClient.post<ApiResponse<PaymentThresholdOverride>>(
      `/Orders/payment-threshold-overrides/${id}/revoke`,
      { reason }
    );
    return data.data;
  },

  getPaymentThresholdOverrideQueue: async (status?: string): Promise<PaymentThresholdOverrideQueueItem[]> => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    const qs = params.toString();
    const { data } = await apiClient.get<ApiResponse<PaymentThresholdOverrideQueueItem[]>>(
      `/Orders/payment-threshold-overrides${qs ? `?${qs}` : ""}`
    );
    return data.data;
  },

  getWorkAuthorizationStatus: async (orderId: string): Promise<WorkAuthorizationStatus> => {
    const { data } = await apiClient.get<ApiResponse<WorkAuthorizationStatus>>(
      `/Orders/${orderId}/work-authorization-status`
    );
    return data.data;
  },

  createOrderAmendment: async (
    orderId: string,
    payload: CreateOrderAmendmentRequest
  ): Promise<OrderAmendment> => {
    const { data } = await apiClient.post<ApiResponse<OrderAmendment>>(
      `/Orders/${orderId}/amendments`,
      payload
    );
    return data.data;
  },

  previewOrderAmendment: async (
    orderId: string,
    payload: PreviewOrderAmendmentRequest
  ): Promise<OrderAmendmentPreview> => {
    const { data } = await apiClient.post<ApiResponse<OrderAmendmentPreview>>(
      `/Orders/${orderId}/amendments/preview`,
      payload
    );
    return data.data;
  },

  submitOrderAmendment: async (
    orderId: string,
    amendmentId: string,
    payload: SubmitOrderAmendmentRequest
  ): Promise<OrderAmendment> => {
    const { data } = await apiClient.post<ApiResponse<OrderAmendment>>(
      `/Orders/${orderId}/amendments/${amendmentId}/submit`,
      payload
    );
    return data.data;
  },

  approveOrderAmendment: async (
    orderId: string,
    amendmentId: string,
    payload: ApproveOrderAmendmentRequest
  ): Promise<{ amendment: OrderAmendment; warnings: string[] }> => {
    const { data } = await apiClient.post<ApiResponse<OrderAmendment>>(
      `/Orders/${orderId}/amendments/${amendmentId}/approve`,
      payload
    );
    return { amendment: data.data, warnings: data.warnings?.map((warning) => warning.message) ?? [] };
  },

  rejectOrderAmendment: async (
    orderId: string,
    amendmentId: string,
    payload: RejectOrderAmendmentRequest
  ): Promise<OrderAmendment> => {
    const { data } = await apiClient.post<ApiResponse<OrderAmendment>>(
      `/Orders/${orderId}/amendments/${amendmentId}/reject`,
      payload
    );
    return data.data;
  },

  applyOrderAmendment: async (
    orderId: string,
    amendmentId: string,
    payload: ApplyOrderAmendmentRequest
  ): Promise<OrderAmendment> => {
    const { data } = await apiClient.post<ApiResponse<OrderAmendment>>(
      `/Orders/${orderId}/amendments/${amendmentId}/apply`,
      payload
    );
    return data.data;
  },

  getOrderAmendments: async (orderId: string): Promise<OrderAmendment[]> => {
    const { data } = await apiClient.get<ApiResponse<OrderAmendment[]>>(
      `/Orders/${orderId}/amendments`
    );
    return data.data;
  },

  getPendingOrderAmendmentQueue: async (pageNumber: number = 1, pageSize: number = 20): Promise<PagedResult<OrderAmendmentQueueItem>> => {
    const { data } = await apiClient.get<ApiResponse<PagedResult<OrderAmendmentQueueItem>>>(
      "/Orders/amendments/queue",
      { params: { pageNumber, pageSize } }
    );
    return data.data;
  },

  getOrderAmendmentHistory: async (pageNumber: number, pageSize: number): Promise<PagedResult<OrderAmendmentHistoryItem>> => {
    const { data } = await apiClient.get<ApiResponse<PagedResult<OrderAmendmentHistoryItem>>>(
      "/Orders/amendments/history",
      { params: { pageNumber, pageSize } }
    );
    return data.data;
  },

  getOrderAmendmentById: async (
    orderId: string,
    amendmentId: string
  ): Promise<OrderAmendment> => {
    const { data } = await apiClient.get<ApiResponse<OrderAmendment>>(
      `/Orders/${orderId}/amendments/${amendmentId}`
    );
    return data.data;
  },

  confirmOrderWithAllocation: async (
    orderId: string,
    payload: ConfirmOrderWithAllocationRequest
  ): Promise<Order> => {
    const { data } = await apiClient.post<ApiResponse<Order>>(
      `/orders/sales-review/${orderId}/confirm-with-allocation`,
      payload
    );
    return data.data;
  },

  publishReadyDate: async (
    orderId: string,
    payload: PublishReadyDateRequest
  ): Promise<Order> => {
    const { data } = await apiClient.post<ApiResponse<Order>>(
      `/orders/sales-review/${orderId}/publish-ready-date`,
      payload
    );
    return data.data;
  },

  getSalesReviewAllocationPreview: async (
    orderId: string
  ): Promise<SalesReviewAllocationPreview> => {
    const { data } = await apiClient.get<ApiResponse<SalesReviewAllocationPreview>>(
      `/orders/sales-review/${orderId}/allocation-preview`
    );
    return data.data;
  },

  confirmDates: async (
    orderId: string,
    payload: { confirmedDeliveryDate?: string | null; confirmedPlantingDate?: string | null }
  ): Promise<Order> => {
    const { data } = await apiClient.post<ApiResponse<Order>>(
      `/orders/sales-review/${orderId}/confirm-dates`,
      payload
    );
    return data.data;
  },

  pushBackAgronomistOrder: async (
    orderId: string,
    payload: PushBackAgronomistOrderRequest
  ): Promise<Order> => {
    const { data } = await apiClient.post<ApiResponse<Order>>(
      `/orders/sales-review/${orderId}/push-back`,
      payload
    );
    return data.data;
  },

  getForecastRequests: async (
    filters: ForecastRequestFilters = {}
  ): Promise<ForecastRequest[]> => {
    const { data } = await apiClient.get<ApiResponse<ForecastRequest[]>>(
      "/orders/sales-review/forecast-requests",
      { params: filters }
    );
    return data.data;
  },

  acknowledgeForecastRequest: async (
    requestId: string
  ): Promise<ForecastRequest> => {
    const { data } = await apiClient.post<ApiResponse<ForecastRequest>>(
      `/orders/sales-review/forecast-requests/${requestId}/acknowledge`
    );
    return data.data;
  },

  getDispatchReadinessQueue: async (params: {
    search?: string;
    pageNumber: number;
    pageSize: number;
  }): Promise<DispatchReadinessPaged> => {
    const searchParams = new URLSearchParams();
    if (params.search) searchParams.set("search", params.search);
    searchParams.set("pageNumber", String(params.pageNumber));
    searchParams.set("pageSize", String(params.pageSize));
    const { data } = await apiClient.get<ApiResponse<DispatchReadinessPaged>>(
      `/orders/sales-review/dispatch-readiness?${searchParams.toString()}`
    );
    return data.data;
  },

  approveDispatch: async (
    payload: ApproveDispatchRequest
  ): Promise<ApproveDispatchResultItem[]> => {
    const { data } = await apiClient.post<ApiResponse<ApproveDispatchResultItem[]>>(
      "/orders/sales-review/approve-dispatch",
      payload
    );
    return data.data;
  },

  refundTrayDeposit: async (
    orderId: string,
    payload: RefundTrayDepositRequest
  ): Promise<Order> => {
    const { data } = await apiClient.post<ApiResponse<Order>>(
      `/Orders/${orderId}/refund-tray-deposit`,
      payload
    );
    return data.data;
  },

  getTrayDepositEligibility: async (orderId: string): Promise<TrayDepositEligibility> => {
    const { data } = await apiClient.get<ApiResponse<TrayDepositEligibility>>(
      `/orders/${orderId}/tray-deposit-eligibility`
    );
    return data.data;
  },
};
